import React, { useState, useEffect, useCallback } from 'react';
import SidebarLayout from '../components/SidebarLayout';
import departmentService from '../services/departmentService';
import fileService from '../services/fileService';
import PdfViewer from '../components/PdfViewer';
import ImageViewer from '../components/ImageViewer';
import { useToast } from '../components/ui/use-toast';
import {
  Users, Search, Download, Eye, File, FileText, Image, Film,
  Music, Archive, Loader2, Building2, ChevronRight, ChevronLeft, RefreshCw,
  FileSpreadsheet, User,
} from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getFileIcon = (mime) => {
  if (!mime) return <File size={16} />;
  if (mime.includes('pdf')) return <FileText size={16} style={{ color: '#ef4444' }} />;
  if (mime.includes('image')) return <Image size={16} style={{ color: '#8b5cf6' }} />;
  if (mime.includes('video')) return <Film size={16} style={{ color: '#f59e0b' }} />;
  if (mime.includes('audio')) return <Music size={16} style={{ color: '#06b6d4' }} />;
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('tar')) return <Archive size={16} style={{ color: '#64748b' }} />;
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return <FileSpreadsheet size={16} style={{ color: '#10b981' }} />;
  return <File size={16} style={{ color: '#6366f1' }} />;
};

const canPreview = (mime) => mime && (mime.includes('image') || mime.includes('pdf'));

// ── Colour palette per department index ────────────────────────────────────
const DEPT_COLORS = [
  { bg: 'linear-gradient(135deg,#6366f1,#8b5cf6)', light: 'rgba(99,102,241,0.1)', border: '#818cf8' },
  { bg: 'linear-gradient(135deg,#0ea5e9,#38bdf8)', light: 'rgba(14,165,233,0.1)', border: '#38bdf8' },
  { bg: 'linear-gradient(135deg,#10b981,#34d399)', light: 'rgba(16,185,129,0.1)', border: '#34d399' },
  { bg: 'linear-gradient(135deg,#f59e0b,#fbbf24)', light: 'rgba(245,158,11,0.1)', border: '#fbbf24' },
  { bg: 'linear-gradient(135deg,#ef4444,#f87171)', light: 'rgba(239,68,68,0.1)', border: '#f87171' },
];

// ── Avatar helper ───────────────────────────────────────────────────────────
const MemberAvatar = ({ name, email, size = 28 }) => {
  const letter = (name || email || '?').charAt(0).toUpperCase();
  const colors = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];
  const color = colors[(name || email || '').charCodeAt(0) % colors.length];
  return (
    <div title={`${name} (${email})`} style={{
      width: size, height: size, borderRadius: '50%',
      background: color, color: '#fff', fontWeight: 700,
      fontSize: size * 0.4, display: 'flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0,
      boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    }}>{letter}</div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────
const DepartmentDrive = () => {
  const { toast } = useToast();
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [storageData, setStorageData] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Viewer
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [currentPdfFile, setCurrentPdfFile] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImageFile, setCurrentImageFile] = useState(null);

  // ── Load departments ──────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [deptData, storage] = await Promise.all([
          departmentService.getMyDepartments(),
          fileService.getStorageStats(),
        ]);
        setDepartments(deptData.departments || []);
        setStorageData(storage);
        if (deptData.departments?.length > 0) {
          setSelectedDept(deptData.departments[0]);
        }
      } catch {
        toast({ variant: 'destructive', title: 'Error', description: 'Gagal memuat data departemen' });
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line
  }, []);

  // ── Load files when dept or search changes ─────────────────────────────
  const loadFiles = useCallback(async () => {
    if (!selectedDept) return;
    try {
      setFilesLoading(true);
      const data = await departmentService.getDepartmentFiles(selectedDept.id, search);
      setFiles(data.files || []);
      setCurrentPage(1);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Gagal memuat file departemen' });
    } finally {
      setFilesLoading(false);
    }
  }, [selectedDept, search]); // eslint-disable-line

  useEffect(() => {
    const timer = setTimeout(loadFiles, 300);
    return () => clearTimeout(timer);
  }, [loadFiles]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDownload = async (file) => {
    try {
      toast({ title: 'Mengunduh...', description: file.original_name });
      await departmentService.downloadDepartmentFile(selectedDept.id, file.id, file.original_name);
      toast({ title: 'Berhasil', description: 'File berhasil diunduh' });
    } catch {
      toast({ variant: 'destructive', title: 'Gagal', description: 'Gagal mengunduh file' });
    }
  };

  const handlePreview = (file) => {
    const previewUrl = `/api/departments/${selectedDept.id}/files/${file.id}/preview`;
    if (file.mime_type?.includes('pdf')) {
      setCurrentPdfFile({ id: file.id, name: file.original_name, downloadUrl: previewUrl });
      setPdfViewerOpen(true);
    } else if (file.mime_type?.includes('image')) {
      setCurrentImageFile({ id: file.id, name: file.original_name, downloadUrl: previewUrl });
      setImageViewerOpen(true);
    }
  };

  const deptColor = (idx) => DEPT_COLORS[idx % DEPT_COLORS.length];

  // ── Empty state ──────────────────────────────────────────────────────────
  if (!loading && departments.length === 0) {
    return (
      <SidebarLayout storageData={storageData}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 80, height: 80, borderRadius: 24, margin: '0 auto 20px',
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(99,102,241,0.3)',
            }}>
              <Building2 size={36} color="#fff" />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
              Anda belum bergabung ke departemen mana pun
            </h2>
            <p style={{ color: '#94a3b8', fontSize: 14, maxWidth: 340, margin: '0 auto' }}>
              Hubungi administrator untuk ditambahkan ke departemen agar dapat berkolaborasi.
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SidebarLayout storageData={{ storage: null }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <Loader2 size={36} style={{ color: '#6366f1', animation: 'spin 1s linear infinite' }} />
        </div>
      </SidebarLayout>
    );
  }

  // ── Stats & Pagination ─────────────────────────────────────────────────────────────────
  const owners = [...new Set(files.map(f => f.owner_email))];
  const totalSize = files.reduce((sum, f) => sum + (f.file_size || 0), 0);
  
  const totalPages = Math.ceil(files.length / itemsPerPage) || 1;
  const paginatedFiles = files.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <SidebarLayout storageData={storageData}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

        {/* ── Top Bar ── */}
        <div style={{
          padding: '0 32px', height: 68, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(226,232,240,0.8)',
          position: 'sticky', top: 0, zIndex: 20,
          boxShadow: '0 1px 8px rgba(0,0,0,0.04)',
        }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#94a3b8', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Building2 size={14} /> Departemen
            </span>
            {selectedDept && (
              <>
                <ChevronRight size={14} color="#cbd5e1" />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>{selectedDept.name}</span>
              </>
            )}
          </nav>
          <button
            onClick={loadFiles}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 10,
              background: '#f8fafc', border: '1px solid #e2e8f0',
              cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 500,
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#94a3b8'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <main style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>

          {/* ── Page Header ── */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 12px rgba(99,102,241,0.3)',
              }}>
                <Building2 size={20} color="#fff" />
              </div>
              <div>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Department Drive
                </h1>
                <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
                  Kolaborasi & berbagi file antar anggota departemen
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

            {/* ── Left: Dept List ── */}
            <div style={{ width: 220, flexShrink: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                Departemen Saya
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {departments.map((dept, idx) => {
                  const clr = deptColor(idx);
                  const active = selectedDept?.id === dept.id;
                  return (
                    <button
                      key={dept.id}
                      onClick={() => { setSelectedDept(dept); setSearch(''); }}
                      style={{
                        width: '100%', textAlign: 'left', padding: '12px 14px',
                        borderRadius: 14, border: active ? `1.5px solid ${clr.border}` : '1.5px solid #e2e8f0',
                        background: active ? clr.light : '#fff',
                        cursor: 'pointer', transition: 'all 0.2s ease',
                        boxShadow: active ? `0 2px 12px ${clr.border}33` : 'none',
                      }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = '#94a3b8'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = '#e2e8f0'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: clr.bg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Building2 size={15} color="#fff" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {dept.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            {dept.member_count} anggota
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* ── Members of selected dept ── */}
              {selectedDept?.members?.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                    Anggota
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedDept.members.map(m => (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 10, background: '#f8fafc' }}>
                        <MemberAvatar name={m.username} email={m.email} size={28} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.username}
                          </div>
                          <div style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.email}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Right: Files Panel ── */}
            <div style={{ flex: 1, minWidth: 0 }}>

              {/* Stats Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Total File', value: files.length, icon: <File size={16} color="#6366f1" />, bg: 'rgba(99,102,241,0.08)' },
                  { label: 'Total Ukuran', value: fmtSize(totalSize), icon: <Archive size={16} color="#0ea5e9" />, bg: 'rgba(14,165,233,0.08)' },
                  { label: 'Kontributor', value: owners.length, icon: <Users size={16} color="#10b981" />, bg: 'rgba(16,185,129,0.08)' },
                ].map(s => (
                  <div key={s.label} style={{
                    background: '#fff', borderRadius: 14, padding: '14px 16px',
                    border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {s.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Search */}
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Cari file di departemen ini..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px 10px 38px',
                    borderRadius: 12, border: '1px solid #e2e8f0',
                    fontSize: 14, outline: 'none', background: '#fff',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              {/* File Table */}
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.04)' }}>

                {/* Table Header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 100px',
                  padding: '12px 20px', background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: 11, fontWeight: 700, color: '#64748b',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                  <span>Nama File</span>
                  <span>Pemilik</span>
                  <span>Ukuran · Tanggal</span>
                  <span style={{ textAlign: 'right' }}>Aksi</span>
                </div>

                {/* Rows */}
                {filesLoading ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <Loader2 size={28} style={{ color: '#6366f1', animation: 'spin 1s linear infinite' }} />
                    <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 12 }}>Memuat file...</p>
                  </div>
                ) : files.length === 0 ? (
                  <div style={{ padding: 48, textAlign: 'center' }}>
                    <File size={36} style={{ color: '#cbd5e1', marginBottom: 12 }} />
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#64748b', margin: 0 }}>
                      {search ? 'Tidak ada file yang sesuai pencarian' : 'Belum ada file di departemen ini'}
                    </p>
                    <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                      {!search && 'Anggota departemen belum mengupload file apapun'}
                    </p>
                  </div>
                ) : (
                  paginatedFiles.map((file, i) => (
                    <div
                      key={file.id}
                      style={{
                        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 100px',
                        padding: '13px 20px', alignItems: 'center',
                        borderBottom: i < paginatedFiles.length - 1 ? '1px solid #f1f5f9' : 'none',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {/* File Name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{ flexShrink: 0 }}>{getFileIcon(file.mime_type)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {file.original_name}
                          </div>
                          {file.is_owner && (
                            <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600, background: 'rgba(16,185,129,0.1)', padding: '1px 6px', borderRadius: 99 }}>
                              Milik Saya
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Owner */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MemberAvatar name={file.owner_username} email={file.owner_email} size={22} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {file.owner_username}
                          </div>
                        </div>
                      </div>

                      {/* Size + Date */}
                      <div>
                        <div style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{fmtSize(file.file_size)}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(file.created_at)}</div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        {canPreview(file.mime_type) && (
                          <button
                            onClick={() => handlePreview(file)}
                            title="Pratinjau"
                            style={{
                              width: 30, height: 30, borderRadius: 8,
                              background: 'rgba(99,102,241,0.08)',
                              border: '1px solid rgba(99,102,241,0.2)',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#6366f1', transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.18)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
                          >
                            <Eye size={13} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(file)}
                          title="Unduh"
                          style={{
                            width: 30, height: 30, borderRadius: 8,
                            background: 'rgba(16,185,129,0.08)',
                            border: '1px solid rgba(16,185,129,0.2)',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#10b981', transition: 'all 0.2s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.18)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.08)'; }}
                        >
                          <Download size={13} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination Controls */}
              {!filesLoading && files.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px 4px', marginTop: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      Menampilkan {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, files.length)} dari {files.length} file
                    </span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      style={{
                        padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                        fontSize: 13, color: '#475569', background: '#fff', outline: 'none', cursor: 'pointer'
                      }}
                    >
                      <option value={5}>5 / halaman</option>
                      <option value={10}>10 / halaman</option>
                      <option value={20}>20 / halaman</option>
                      <option value={50}>50 / halaman</option>
                    </select>
                  </div>
                  
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '6px', borderRadius: 8,
                        background: currentPage === 1 ? '#f8fafc' : '#fff',
                        border: '1px solid #e2e8f0',
                        color: currentPage === 1 ? '#cbd5e1' : '#475569',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => { if (currentPage !== 1) e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={e => { if (currentPage !== 1) e.currentTarget.style.background = '#fff'; }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: 13, fontWeight: 500, color: '#0f172a' }}>
                      {currentPage} / {totalPages}
                    </div>

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '6px', borderRadius: 8,
                        background: currentPage === totalPages ? '#f8fafc' : '#fff',
                        border: '1px solid #e2e8f0',
                        color: currentPage === totalPages ? '#cbd5e1' : '#475569',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => { if (currentPage !== totalPages) e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={e => { if (currentPage !== totalPages) e.currentTarget.style.background = '#fff'; }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Info note */}
              <div style={{ marginTop: 16, padding: '10px 16px', borderRadius: 10, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <User size={14} style={{ color: '#6366f1', flexShrink: 0 }} />
                <p style={{ fontSize: 12, color: '#6366f1', margin: 0 }}>
                  File departemen bersifat <strong>read-only</strong>. Anda dapat melihat dan mengunduh file milik anggota lain, namun tidak dapat menghapus atau mengubahnya.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Viewers */}
      <PdfViewer open={pdfViewerOpen} onClose={() => setPdfViewerOpen(false)} file={currentPdfFile} />
      <ImageViewer open={imageViewerOpen} onClose={() => setImageViewerOpen(false)} file={currentImageFile} />
    </SidebarLayout>
  );
};

export default DepartmentDrive;
